/**
 * An item's standing, relative to a cycle (migration 1019, roadmap S-37).
 *
 * An item has no rank of its own, and rank mode belongs to the cycle, not the
 * item (glossary, Rank of Record). The same item can be fixed at #3 in this
 * year's plan and ranked again for next year's. These are the pure rules; the
 * reads are in itemCycleStandingService.ts.
 */

import type { Item, ItemFrame, ItemLanguageType } from '../types/prioritization/lifecycle';
import type { ItemRanking } from './itemRankings';

export type CycleRankMode = 'ranking' | 'in_execution' | 'closed';

/** What a plan holds for an item: the version it authorized, until it is amended. */
export interface ItemHeldValues {
  /** What the item said when the plan authorized it (migration 1060). */
  title: string | null;
  description: string | null;
  itemCategory: string | null;
  altitudeLevel: number | null;
  effortCapacityUnits: number | null;
  workType: string | null;
  launchType: string | null;
  investmentType: string | null;
  /** The additional categories of the version the plan holds (912's shape). */
  additionalCategories: HeldCategory[];
}

export interface HeldCategory {
  id: string;
  category: string;
  altitude: string | number | null;
  title: string | null;
  description: string | null;
  is_primary: boolean | null;
}

export interface ItemPlanHold {
  planCycleId: string;
  planName: string;
  /** The cycle the plan was built from; null when the database ties it to none. */
  cycleId: string | null;
  cycleName: string | null;
  state: 'in_force' | 'starts_later' | 'being_prepared' | 'replaced';
  rank: number | null;
  aboveTheLine: boolean | null;
  authorizedAt: string | null;
  /** The version this plan holds. */
  version: number | null;
  latestVersion: number | null;
  /** The person asking may amend this plan (938's rule), so the page may offer it. */
  mayAmend: boolean;
  held: ItemHeldValues;
}

export interface ItemCycleStanding {
  /** The cycle asked about, or null when none was. */
  cycle: { id: string; name: string; mode: CycleRankMode; inCycle: boolean } | null;
  /** Every cycle the item is in. */
  cycles: Array<{ id: string; name: string; mode: CycleRankMode }>;
  /** Every plan that holds the item and has not been replaced. */
  plans: ItemPlanHold[];
  latestVersion: number | null;
}

export const RANK_MODE_LABEL: Record<CycleRankMode, string> = {
  ranking: 'Ranking',
  in_execution: 'In execution',
  closed: 'Closed',
};

/** The cycle the page is opened from: the one named, else the cycle of the plan named. */
export function cycleOpenedFrom(args: { cycleParam: string | null; planParam: string | null; plans: ItemPlanHold[] }): string | null {
  if (args.cycleParam) return args.cycleParam;
  if (!args.planParam) return null;
  return args.plans.find((p) => p.planCycleId === args.planParam)?.cycleId ?? null;
}

/** The named cycle, when the item is in it. */
export function cycleInContext(standing: ItemCycleStanding | null) {
  return standing?.cycle?.inCycle ? standing.cycle : null;
}

/**
 * Which plans lock this page.
 *
 * In a cycle still ranking: none. What another cycle fixed is a fact there.
 * In a cycle with its own plan in force: that plan alone.
 * With no cycle, or one that is neither: every plan in force, as the server has it.
 */
export function plansThatLock<T extends { cycleId?: string | null }>(plans: T[], standing: ItemCycleStanding | null): T[] {
  const cycle = cycleInContext(standing);
  if (!cycle) return plans;
  if (cycle.mode === 'ranking') return [];
  const own = plans.filter((p) => p.cycleId === cycle.id);
  return own.length > 0 ? own : plans;
}

/** "#3 of record in the FY26 plan": a fixed rank, as a fact. */
export function rankOfRecordFact(p: Pick<ItemPlanHold, 'rank' | 'planName' | 'aboveTheLine'>): string {
  const rank = p.rank != null ? `#${p.rank} of record` : 'Of record';
  return `${rank} in ${p.planName}${p.aboveTheLine === false ? ', below the line' : ''}`;
}

/** The plans in this standing other than the ones locking the page: facts, not locks. */
export function plansAsFacts(standing: ItemCycleStanding | null, lockingPlanIds: string[]): ItemPlanHold[] {
  return (standing?.plans ?? []).filter((p) => !lockingPlanIds.includes(p.planCycleId));
}

/** A priority list's rank in this cycle: the list's track tells which cycle it belongs to. */
export function listsInCycle(rankings: ItemRanking[], cycleId: string, cycleOfTrack: (trackId: string) => string | null | undefined): ItemRanking[] {
  return rankings.filter(
    (r) => (r.standing === 'agreed' || r.standing === 'proposed') && cycleOfTrack(r.sourceHref?.split('/').pop() ?? '') === cycleId,
  );
}

/** The item as the plan holds it, for showing it. Saves never go through this. */
export function itemAsHeld<T extends Item>(item: T, held: ItemHeldValues): T {
  const frames: ItemFrame[] = held.additionalCategories.map((c) => ({
    id: c.id,
    sourceItemId: item.id,
    frameType: c.category as ItemLanguageType,
    title: c.title ?? '',
    description: c.description ?? '',
    createdBy: '',
    createdAt: new Date(0),
    isCanonical: !!c.is_primary,
    altitudeLevel: c.altitude != null ? String(c.altitude) : undefined,
  }));
  return {
    ...item,
    title: held.title ?? item.title,
    description: held.description ?? undefined,
    frames,
    canonicalFrameId: frames.find((f) => f.isCanonical)?.id,
    itemCategory: held.itemCategory ?? undefined,
    altitudeLevel: held.altitudeLevel ?? undefined,
    effortCapacityUnits: held.effortCapacityUnits ?? undefined,
    work_type: held.workType ?? undefined,
    launch_type: held.launchType ?? undefined,
    investment_type: held.investmentType ?? undefined,
  } as T;
}

/** A link to an item from inside a cycle or a plan, so the page knows where it was opened from. */
export function itemInContextHref(itemId: string, context: { cycleId?: string | null; planCycleId?: string | null; tab?: string | null }): string {
  const params = new URLSearchParams();
  if (context.tab) params.set('tab', context.tab);
  if (context.cycleId) params.set('cycle', context.cycleId);
  if (context.planCycleId) params.set('plan', context.planCycleId);
  const query = params.toString();
  return `/prioritization/items/${itemId}${query ? `?${query}` : ''}`;
}
